/** Budgets include response bodies; the cast budget covers the entire fallback chain. */
export const CINEMETA_DEADLINE_MS = 15_000;
export const CAST_DEADLINE_MS = 45_000;

/** Own the deadline outside fetch: even a transport ignoring abort must settle its waiter.
 * The operation must check this signal before publishing results or starting more work. */
export function catalogueDeadline<T>(
  parent: AbortSignal,
  ms: number,
  label: string,
  run: (signal: AbortSignal) => Promise<T>
): Promise<T> {
  if (parent.aborted) return Promise.reject(parent.reason);
  const controller = new AbortController();
  return new Promise<T>((resolve, reject) => {
    let settled = false;
    const finish = (accept: () => void) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      parent.removeEventListener('abort', cancelled);
      accept();
    };
    const stop = (reason: unknown) => {
      // Reject before dispatching abort so a transport's generic AbortError cannot
      // hide a deadline as a component teardown in loading-state consumers.
      finish(() => reject(reason));
      controller.abort(reason);
    };
    const cancelled = () => stop(parent.reason);
    const timer = setTimeout(() => stop(new DOMException(`${label} timed out. Try again.`, 'TimeoutError')), ms);
    parent.addEventListener('abort', cancelled, { once: true });
    void Promise.resolve().then(() => {
      controller.signal.throwIfAborted();
      return run(controller.signal);
    }).then(value => finish(() => resolve(value)), error => finish(() => reject(error)));
  });
}
